import { logger } from '@/sentry.config'
import { OrderReportResponse } from '@/types/OrderReportTypes'
import { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { getPatientInfoFromForm } from '@/utils/orderFormUtils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAllOrderReports, getOrderReportById } from '../../features/report/services/reportClient'
import PDFViewer from '../PDFViewer'

interface OrderReportProps {
  orderId?: string
  orderData?: OrderFormInfoData
}

/**
 * OrderReport Component
 *
 * Displays the lab test results PDF inline within the order confirmation page.
 * This component is shown when the order status is "Result Ready".
 * Supports multiple reports with selection capability.
 */
const OrderReport: React.FC<OrderReportProps> = ({ orderId, orderData }) => {
  const [reports, setReports] = useState<OrderReportResponse[]>([])
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)
  const [labResultPdfBytes, setLabResultPdfBytes] = useState<Uint8Array | null>(null)
  const [isLoadingReports, setIsLoadingReports] = useState(true)
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const [reportsError, setReportsError] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const patientName = getPatientInfoFromForm(orderData ?? null).name || null

  // Compute caller from ordering provider name
  const caller = useMemo(() => {
    if (orderData?.orderingProvider?.name) {
      const firstName = orderData.orderingProvider.name.firstName || ''
      const lastName = orderData.orderingProvider.name.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || 'spa'
    }
    return 'spa'
  }, [orderData?.orderingProvider?.name])

  // Find the latest report based on generated_at
  const latestReport = useMemo(() => {
    if (reports.length === 0) return null

    return reports.reduce((latest, report) => {
      const latestDate = latest.generated_at ? new Date(latest.generated_at).getTime() : 0
      const reportDate = report.generated_at ? new Date(report.generated_at).getTime() : 0

      // Handle invalid dates (NaN) - prefer valid dates over invalid ones
      const latestIsValid = !isNaN(latestDate) && latestDate > 0
      const reportIsValid = !isNaN(reportDate) && reportDate > 0

      // If current report has invalid date and latest has valid date, keep latest
      if (!reportIsValid && latestIsValid) return latest
      // If latest has invalid date and current report has valid date, use current report
      if (reportIsValid && !latestIsValid) return report
      // If both are invalid or both are valid, compare normally
      return reportDate > latestDate ? report : latest
    })
  }, [reports])

  // Fetch all reports for the order
  // Use a ref to track the current request and prevent race conditions
  const currentReportsRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orderId) {
      // If orderId is missing, stop loading and show appropriate state
      setIsLoadingReports(false)
      setReportsError('Order ID is required to fetch reports')
      currentReportsRequestRef.current = null
      return
    }

    // Create a unique request ID for this fetch
    const requestId = `${orderId}-${caller}`
    currentReportsRequestRef.current = requestId

    const fetchAllReports = async () => {
      try {
        setIsLoadingReports(true)
        setReportsError(null)

        const allReports = await getAllOrderReports(orderId, caller)

        // Only update state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          setReports(allReports)

          // Auto-select latest report if available
          if (allReports.length > 0) {
            const latest = allReports.reduce((latest, report) => {
              const latestDate = latest.generated_at ? new Date(latest.generated_at).getTime() : 0
              const reportDate = report.generated_at ? new Date(report.generated_at).getTime() : 0

              // Handle invalid dates (NaN) - prefer valid dates over invalid ones
              const latestIsValid = !isNaN(latestDate) && latestDate > 0
              const reportIsValid = !isNaN(reportDate) && reportDate > 0

              // If current report has invalid date and latest has valid date, keep latest
              if (!reportIsValid && latestIsValid) return latest
              // If latest has invalid date and current report has valid date, use current report
              if (reportIsValid && !latestIsValid) return report
              // If both are invalid or both are valid, compare normally
              return reportDate > latestDate ? report : latest
            })
            setSelectedReportId(latest.id)
          }
        }
      } catch (error) {
        // Only update state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          logger.error('Failed to fetch reports', {
            orderId,
            error: error instanceof Error ? error.message : String(error),
          })
          setReportsError(error instanceof Error ? error.message : 'Failed to fetch reports')
        }
      } finally {
        // Only update loading state if this request is still the current one (not stale)
        if (currentReportsRequestRef.current === requestId) {
          setIsLoadingReports(false)
        }
      }
    }

    fetchAllReports()

    // Cleanup function to mark this request as stale when dependencies change
    return () => {
      currentReportsRequestRef.current = null
    }
  }, [orderId, caller])

  // Fetch PDF for selected report
  // Use a ref to track the current request and prevent race conditions
  const currentRequestRef = useRef<string | null>(null)

  useEffect(() => {
    if (!orderId || !selectedReportId) {
      setLabResultPdfBytes(null)
      setIsLoadingPDF(false)
      return
    }

    // Create a unique request ID for this fetch
    const requestId = `${orderId}-${selectedReportId}-${caller}`
    currentRequestRef.current = requestId

    const fetchLabResultPDF = async () => {
      try {
        setIsLoadingPDF(true)
        setPdfError(null)

        const pdfBytes = await getOrderReportById(orderId, selectedReportId, caller)

        // Only update state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          setLabResultPdfBytes(pdfBytes)
        }
      } catch (error) {
        // Only update state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          logger.error('Failed to fetch lab result PDF', {
            orderId,
            reportId: selectedReportId,
            error: error instanceof Error ? error.message : String(error),
          })
          setPdfError(error instanceof Error ? error.message : 'Failed to fetch lab results PDF')
          setLabResultPdfBytes(null)
        }
      } finally {
        // Only update loading state if this request is still the current one (not stale)
        if (currentRequestRef.current === requestId) {
          setIsLoadingPDF(false)
        }
      }
    }

    fetchLabResultPDF()

    // Cleanup function to mark this request as stale when dependencies change
    return () => {
      currentRequestRef.current = null
    }
  }, [orderId, selectedReportId, caller])

  // Get selected report details
  const selectedReport = useMemo(() => {
    return reports.find((r) => r.id === selectedReportId) || null
  }, [reports, selectedReportId])

  // Format date for display
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      // Check if the date is valid - invalid dates have NaN for getTime()
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return 'N/A'
    }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Report Information Header */}
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="page-navigation-title mb-3">Report Information</h3>
        <dl className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="form-label">Order ID</dt>
            <dd className="body-text mt-1 font-mono text-sm font-semibold text-gray-900">{orderId || 'N/A'}</dd>
          </div>
          <div>
            <dt className="form-label">Patient Name</dt>
            <dd className="body-text mt-1">{patientName || 'N/A'}</dd>
          </div>
          <div>
            <dt className="form-label">Results Date</dt>
            <dd className="body-text mt-1">{formatDate(selectedReport?.generated_at)}</dd>
          </div>
          <div>
            <dt className="form-label">Status</dt>
            <dd className="mt-1">
              <span className="badge-text inline-flex items-center rounded-full bg-[#D3E8BE] px-2.5 py-0.5 text-green-800">
                Complete
              </span>
            </dd>
          </div>
        </dl>

        {/* Reports List */}
        {isLoadingReports ? (
          <div className="flex items-center justify-center py-4">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
            <p className="body-text ml-2 text-gray-600">Loading reports...</p>
          </div>
        ) : reportsError ? (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3">
            <p className="body-text text-red-800">{reportsError}</p>
          </div>
        ) : reports.length > 0 ? (
          <div className="mt-4">
            <h4 className="form-label mb-2">Available Reports</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                      Report Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                      Version
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700">
                      Generated At
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {reports.map((report) => {
                    const isSelected = report.id === selectedReportId
                    const isLatest = report.id === latestReport?.id
                    return (
                      <tr
                        key={report.id}
                        onClick={() => setSelectedReportId(report.id)}
                        className={`cursor-pointer transition-colors ${
                          isSelected ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center">
                            <span className="body-text font-medium text-gray-900">{report.report_type || 'N/A'}</span>
                            {isLatest && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                Latest
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="body-text text-gray-900">
                            {report.version !== null && report.version !== undefined ? String(report.version) : 'N/A'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="body-text text-gray-900">{formatDate(report.generated_at)}</span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="body-text text-gray-600">No reports available for this order.</p>
          </div>
        )}
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
        {isLoadingPDF ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-green-600 border-r-transparent"></div>
              <p className="body-text mt-2 text-gray-600">Loading results PDF...</p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="body-text mt-4 font-medium text-red-600">Unable to load results PDF</p>
              <p className="small-text mt-2 text-gray-500">{pdfError}</p>
            </div>
          </div>
        ) : labResultPdfBytes ? (
          <PDFViewer pdfBytes={labResultPdfBytes} filename="Lab-Results.pdf" className="h-full" />
        ) : selectedReportId ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="body-text mt-4 font-medium text-gray-600">No PDF available for selected report</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OrderReport
